import React, { useMemo } from 'react';
import C from 'classnames';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLicensedCapacity } from 'infrastructure/hooks/use-licensed-capacity';

import CapacityGlyph from './capacity-glyph';
import s from './styles.module.scss';

import type { ICapacityPresentation } from './types';

interface IProps {
  patientGuid?: string | null;
  isOnCall: boolean;
  /** `compact` shortens the label for the ~350px Connect popup. */
  variant?: 'full' | 'compact';
  /** Dashboard only. Adds focus + tooltip. The popup stays inert. */
  interactive?: boolean;
  className?: string;
}

interface ICounts {
  licensedAvailable?: number | null;
  licensedOnline?: number | null;
}

/**
 * Maps the response to one of the five visible states. Pure and exported so the
 * mapping can be unit tested without rendering anything.
 *
 * A `null` count means capacity is UNDETERMINABLE, not zero — the backend
 * returns 200 with nulls so a 10s poll doesn't fire an error toast. A real 0
 * and an unknown must never look the same.
 */
export const getCapacityState = (
  counts: ICounts,
  isLoading: boolean,
): ICapacityPresentation => {
  const { licensedAvailable: free, licensedOnline: online } = counts;

  const isResolved = typeof free === 'number' && typeof online === 'number';

  if (!isResolved) {
    return isLoading
      ? { state: 'pending', tone: 'pending', glyph: 'ring' }
      : { state: 'unknown', tone: 'idle', glyph: 'dashed' };
  }
  if (free > 0) return { state: 'free', tone: 'free', glyph: 'solid' };
  // free === 0 — the distinction that matters most to the agent:
  // someone licensed is working but occupied, vs nobody licensed is logged in.
  if (online > 0) return { state: 'busy', tone: 'busy', glyph: 'half' };
  return { state: 'none', tone: 'idle', glyph: 'ring' };
};

/**
 * Both figures resolve together or neither does. Modelled as a union rather than
 * two independently-nullable numbers so a half-resolved subset is not
 * representable: the readout has no honest sentence for "1 has a free line, and
 * we cannot say how many speak it", and the previous shape let that state
 * dangle a pronoun with no antecedent. It also removes the casts that
 * `number | null` forced at every use.
 */
type ILanguageChip =
  | { resolved: false }
  | { resolved: true; available: number; online: number };

/**
 * The language chip's own state. Never feeds the container tone or glyph:
 * the chip is a subset, and letting it move them would tell the agent the
 * transfer cannot happen when it can. Routing drops the language requirement
 * after ~20s and still lands on a licensed nurse, so "no Spanish speaker free"
 * is never a reason to withhold the transfer.
 *
 * Takes the primary counts to clamp the subset into them rather than trusting
 * the nesting invariant. The two figures come from SEPARATE cache keys
 * (`…:{state}:any` and `…:{state}:es`) whose on-demand refreshes are
 * independent, so one can be fresher than the other and the subset can arrive
 * larger than its parent. Left unclamped that renders a green "ES 2" beside a
 * primary reading "0 free" — self-contradictory, and it leaves the agent
 * trusting whichever figure they happened to read. Clamping cannot invent
 * capacity; it only refuses to claim more than the pool holds.
 *
 * `null` means render no chip at all.
 */
export const getLanguageChipState = (
  language: string | null | undefined,
  counts: {
    languageAvailable?: number | null;
    languageOnline?: number | null;
  },
  primary: ICounts,
): ILanguageChip | null => {
  if (!language) return null;

  const { languageAvailable, languageOnline } = counts;
  const { licensedAvailable, licensedOnline } = primary;

  // Any missing figure makes the whole subset undeterminable. A null count is
  // "couldn't tell", never zero, and reporting half of a pair would be worse
  // than reporting none of it.
  if (
    typeof languageAvailable !== 'number' ||
    typeof languageOnline !== 'number' ||
    typeof licensedAvailable !== 'number' ||
    typeof licensedOnline !== 'number'
  ) {
    return { resolved: false };
  }

  return {
    resolved: true,
    available: Math.min(languageAvailable, licensedAvailable),
    online: Math.min(languageOnline, licensedOnline),
  };
};

const LicensedCapacity: React.FC<IProps> = ({
  patientGuid,
  isOnCall,
  variant = 'full',
  interactive = false,
  className,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, dataUpdatedAt } = useLicensedCapacity({
    patientGuid,
    canLoad: isOnCall,
  });

  const {
    licensedAvailable,
    licensedOnline,
    state: usState,
    language,
    languageAvailable,
    languageOnline,
  } = data ?? {};

  const { state, tone, glyph } = useMemo(
    () => getCapacityState({ licensedAvailable, licensedOnline }, isLoading),
    [licensedAvailable, licensedOnline, isLoading],
  );

  const languageChip = useMemo(
    () =>
      getLanguageChipState(
        language,
        { languageAvailable, languageOnline },
        { licensedAvailable, licensedOnline },
      ),
    [
      language,
      languageAvailable,
      languageOnline,
      licensedAvailable,
      licensedOnline,
    ],
  );

  if (!isOnCall) return null;

  const hasNumbers = state === 'free' || state === 'busy' || state === 'none';

  // Single source of truth for "is the chip on screen". The width bump and the
  // shortened compact label both buy room FOR the chip, so they must be gated on
  // the same expression that renders it — otherwise a response carrying a
  // language with unresolved counts would pay 56px and drop the label's noun for
  // a chip that never appears. Today the endpoint nulls `language` whenever the
  // counts are null, so that combination does not occur; this makes the layout
  // independent of that invariant rather than quietly coupled to it.
  const showLanguage = hasNumbers && Boolean(languageChip);

  // `state` is non-null whenever the counts are, so the suffix only needs a
  // fallback for pending and unknown. nowrap inside a fixed-width container
  // means it can never reflow. Keys stay literal — i18next types them.
  const label = () => {
    if (!usState) return t('licensedCapacity.label');

    if (variant !== 'compact') {
      return t('licensedCapacity.labelWithState', { state: usState });
    }
    // Compact fills the 350px modal, so the chip's ~50px has to come out of the
    // label. Dropping the noun keeps BOTH figures, their units and the state
    // code; letting the ellipsis run would have cut the state code instead. The
    // glyph and the group's aria-label still identify the readout.
    return showLanguage
      ? t('licensedCapacity.labelCompactLanguage', { state: usState })
      : t('licensedCapacity.labelCompact', { state: usState });
  };

  const ageInSeconds = dataUpdatedAt
    ? Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000))
    : 0;

  // Spelled out ("Spanish"), not the code — a screen reader saying "E S" is
  // worse than the word. `defaultValue` is the guard, not a nicety: the routable
  // set is defined server-side, so a language added there without a `languages`
  // entry would otherwise read out its own translation key. It also satisfies
  // the `t` overload for a non-literal key, so this needs no cast.
  const languageName = language
    ? t(`languages.${language}`, { defaultValue: language.toUpperCase() })
    : '';

  /**
   * The subset sentences. Referents chain — "of them" is the online licensed
   * nurses, "of those" is the speakers among them — which is what stops a bare
   * "them" reading as the wrong population. Each sentence carries its own
   * `count` because one string cannot govern two verb agreements.
   */
  const spokenLanguage = () => {
    if (!languageChip) return '';

    if (!languageChip.resolved) {
      return ` ${t('licensedCapacity.a11y.languageUnknown', {
        language: languageName,
      })}`;
    }

    // Both narrowed to `number` by `resolved`, so no cast and no dead branch for
    // a half-resolved pair the type cannot express.
    const { available, online } = languageChip;

    // Only promise a fallback when there is somebody to fall back to.
    // `hasNumbers` includes `state === 'none'` — nobody licensed online at all —
    // where "the transfer still connects" would be flatly untrue.
    const fallback =
      (licensedOnline ?? 0) > 0
        ? ` ${t('licensedCapacity.a11y.languageFallback')}`
        : '';

    // Speakers online but all busy. Distinct from "nobody speaks it": the agent
    // may only need to wait, and "None of them speaks Spanish" would be false
    // whenever `online > 0`.
    if (available === 0 && online > 0) {
      return ` ${t('licensedCapacity.a11y.languageOnlineValue', {
        count: online,
        language: languageName,
      })} ${t('licensedCapacity.a11y.languageNoneFree')}${fallback}`;
    }

    if (available === 0) {
      return ` ${t('licensedCapacity.a11y.languageNone', {
        language: languageName,
      })}${fallback}`;
    }

    return ` ${t('licensedCapacity.a11y.languageOnlineValue', {
      count: online,
      language: languageName,
    })} ${t('licensedCapacity.a11y.languageFreeValue', { count: available })}`;
  };

  const spokenValue = () => {
    if (state === 'pending') return t('licensedCapacity.a11y.pending');
    if (state === 'unknown') return t('licensedCapacity.a11y.unknown');

    // Split for the same reason as the subset sentences: one key cannot govern
    // two verb agreements. Pre-existing bug — the combined string picked its
    // plural from the ONLINE count, so "0 has a free line" was read out whenever
    // one nurse was online and none free.
    return `${t('licensedCapacity.a11y.value', {
      count: licensedOnline as number,
      state: usState,
    })} ${t('licensedCapacity.a11y.valueFree', {
      count: licensedAvailable as number,
    })} ${t('licensedCapacity.a11y.valueAge', {
      age: ageInSeconds,
    })}${spokenLanguage()}`;
  };

  const readout = (
    <div
      className={C(s.capacity, s[variant], s[tone], className, {
        [s['has-language']]: showLanguage,
      })}
      // Never announced. A number read aloud every 10s over a live patient
      // call is worse than silence — the value is readable on demand instead.
      aria-live="off"
      role="group"
      aria-label={t('licensedCapacity.a11yGroup')}
      // Deliberately focusable despite being non-interactive: focus is the
      // read-on-demand affordance that replaces announcing the value every 10s.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={interactive ? 0 : undefined}
      data-cy="licensed-capacity"
      data-state={state}
    >
      <CapacityGlyph glyph={glyph} tone={tone} />

      <span className={s.label}>{label()}</span>
      <span className={s.spacer} />

      {hasNumbers ? (
        <>
          <span className={s.slot}>
            <span className={C(s.value, s.primary)}>{licensedAvailable}</span>
            <span className={s.unit}>{t('licensedCapacity.unitFree')}</span>
          </span>
          <span className={s.divider} />
          <span className={s.slot}>
            <span className={s.value}>{licensedOnline}</span>
            <span className={s.unit}>{t('licensedCapacity.unitOnline')}</span>
          </span>

          {/* Subset of the two figures to its left, not a third category. Its
              own colour never reaches the container: the transfer connects
              either way, so this must not read as a blocker. */}
          {showLanguage && languageChip ? (
            <span
              className={C(s.chip, {
                [s['chip-has']]:
                  languageChip.resolved && languageChip.available > 0,
              })}
              data-cy="licensed-capacity-language"
              data-language={language}
            >
              <span className={s['chip-code']}>{language?.toUpperCase()}</span>
              <span className={s['chip-value']}>
                {languageChip.resolved
                  ? languageChip.available
                  : t('licensedCapacity.languageUnknownValue')}
              </span>
            </span>
          ) : null}
        </>
      ) : null}

      {state === 'unknown' ? (
        <span className={s.sentence}>{t('licensedCapacity.unknown')}</span>
      ) : null}

      {state === 'pending' ? (
        <>
          {/* Static, not shimmering. A pulsing element beside a live patient
              call is the wrong kind of movement. Sized to the number clusters
              they stand in for so nothing shifts when the data lands. */}
          <span className={C(s.skeleton, s['skeleton-free'])} />
          <span className={s.divider} />
          <span className={C(s.skeleton, s['skeleton-online'])} />
        </>
      ) : null}

      <span className={s['sr-only']}>{spokenValue()}</span>
    </div>
  );

  if (!interactive) return readout;

  return (
    <Tooltip
      title={spokenValue()}
      placement="bottomLeft"
      trigger={['hover', 'focus']}
      overlayClassName={s.tooltip}
    >
      {readout}
    </Tooltip>
  );
};

export default LicensedCapacity;
